/*
Emails Update, the live recipient lists (XOL-586, plan §3.10, D-2026-10-16d).

Pure ES5 logic, no copy and no Vue: no optional chaining or nullish coalescing
(Babel 6 build). The component (EmailSetup.vue) owns the words; this module owns
the rules, and they mirror the API's resolver (RequestMailRecipients, plan §3.3) so
a column can recompute while the user edits, before the save returns. The server
stays authoritative and is re-read on the next open.

A party's lists (`actions.emails.<party>`, party = towerco | owner | provider):

    additional    { contact_id, email, upload, approval }   an org user (by id) OR a
                                                            typed address, with ticks
    unsubscribed  { contact_id, upload, approval }          an org user the Tower Co
                                                            switched off for this request

A party receives, per alert:  (users with the alert on in their profile
+ Additional entries ticked for it) - Unsubscribed entries ticked for it.
Unsubscribed wins (plan N1). The Tower Co column has the Upload alert only.
------------------------------------------------------------------------ */

export var ALERTS = ['upload', 'approval']

// The lists the SPA writes, one FieldPatch path each (plan §3.5).
export var PARTIES = ['towerco', 'owner', 'provider']
export var LIST_NAMES = ['additional', 'unsubscribed']

/** `emails.owner.additional` */
export function listPath (party, list) {
  return 'emails.' + party + '.' + list
}

/** Every recipient list path, in the order towerco, owner, provider. */
export function recipientPaths () {
  var out = []
  for (var p = 0; p < PARTIES.length; p++) {
    for (var l = 0; l < LIST_NAMES.length; l++) {
      out.push(listPath(PARTIES[p], LIST_NAMES[l]))
    }
  }
  return out
}

/**
 * The alerts a party's column carries when the read model doesn't say: only the owner gets
 * approval-kind mail. The Tower Co never does (Q3). The provider's only approval-kind mail was
 * the file-approved notice, whose trigger is off for now (Carlos, 2026-10-17), so it carries
 * Upload only. The API's `/recipients` `alerts` wins over this when present.
 */
export function alertsFor (party) {
  return party === 'owner' ? ['upload', 'approval'] : ['upload']
}

function isBlank (v) {
  return v === undefined || v === null || String(v).trim() === ''
}

/**
 * The identity of a list entry: `c:<contact_id>` for an org user, `e:<address,
 * lower-cased>` for a typed address. null when it has neither. A re-apply after a
 * 409 matches ops to the server's list by this key.
 */
export function recipientKey (entry) {
  if (!entry || typeof entry !== 'object') {
    return null
  }
  if (entry.contact_id !== undefined && entry.contact_id !== null && entry.contact_id !== '') {
    return 'c:' + Number(entry.contact_id)
  }
  if (!isBlank(entry.email)) {
    return 'e:' + String(entry.email).trim().toLowerCase()
  }
  return null
}

/* ------------------------------------------------------------------------
   E-mail format: a conservative mirror of PHP's FILTER_VALIDATE_EMAIL
------------------------------------------------------------------------ */

var LOCAL_PART = /^[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+(\.[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+)*$/
var DOMAIN_LABEL = /^[A-Za-z0-9]([A-Za-z0-9-]{0,61}[A-Za-z0-9])?$/

/**
 * Is `s` (trimmed) an address the API will accept? ASCII only, one `@`, a dotted
 * domain, no spaces or commas. Stricter is safe: the API is the authority, and a
 * 422 reaches the user through the save notice.
 */
export function isValidEmail (s) {
  if (typeof s !== 'string') {
    return false
  }
  var v = s.trim()
  if (v === '' || v.length > 254) {
    return false
  }
  var at = v.indexOf('@')
  if (at < 1 || at !== v.lastIndexOf('@')) {
    return false
  }
  var local = v.slice(0, at)
  var domain = v.slice(at + 1)
  if (local.length > 64 || !LOCAL_PART.test(local)) {
    return false
  }
  var labels = domain.split('.')
  if (labels.length < 2) {
    return false
  }
  for (var i = 0; i < labels.length; i++) {
    if (!DOMAIN_LABEL.test(labels[i])) {
      return false
    }
  }
  // A numeric top-level label is an IP fragment, not a mailbox domain.
  return !/^[0-9]+$/.test(labels[labels.length - 1])
}

/* ------------------------------------------------------------------------
   Entries and lists (canonical key order: contact_id, email, upload, approval)
------------------------------------------------------------------------ */

/** An Additional entry, keys in the canonical order. Exactly one of contactId / email is set. */
export function additionalEntry (contactId, email, upload, approval) {
  var byContact = contactId !== undefined && contactId !== null
  return {
    contact_id: byContact ? Number(contactId) : null,
    email: byContact ? null : String(email).trim(),
    upload: !!upload,
    approval: !!approval
  }
}

/** An Unsubscribed entry, keys in the canonical order. */
export function unsubscribedEntry (contactId, upload, approval) {
  return {
    contact_id: Number(contactId),
    upload: !!upload,
    approval: !!approval
  }
}

function cloneList (list) {
  return (list || []).map(function (e) {
    var c = {}
    Object.keys(e || {}).forEach(function (k) { c[k] = e[k] })
    return c
  })
}

function indexOfKey (list, key) {
  for (var i = 0; i < list.length; i++) {
    if (recipientKey(list[i]) === key) {
      return i
    }
  }
  return -1
}

/** The entry with this key, or null. */
export function findEntry (list, key) {
  var at = indexOfKey(list || [], key)
  return at === -1 ? null : list[at]
}

function ticked (entry) {
  return !!entry && (entry.upload === true || entry.approval === true)
}

/**
 * `list` with one tick of the entry `key` set. Returns a NEW list. An entry left
 * with no tick is removed (the same as the Remove / Re-subscribe action), except
 * with `keepEmpty` and an org-user entry: Delta 5b keeps a user's Additional entry
 * with every tick false until a Tower Co user removes it with the (X). A typed
 * address never stays empty (it is its own recipient, so it would be nothing).
 */
export function setTick (list, key, alert, value, keepEmpty) {
  var out = cloneList(list)
  var at = indexOfKey(out, key)
  if (at === -1) {
    return out
  }
  out[at][alert] = !!value
  var byContact = out[at].contact_id !== undefined && out[at].contact_id !== null
  if (!ticked(out[at]) && !(keepEmpty && byContact)) {
    out.splice(at, 1)
  }
  return out
}

/**
 * Keep Additional and Unsubscribed exclusive per contact and tick (plan §3.10).
 *
 *   mode 'add'          the contact RECEIVES `alert`: it is ticked in Additional and
 *                       cleared in Unsubscribed
 *   mode 'unsubscribe'  the contact does NOT receive `alert`: ticked in Unsubscribed
 *                       and cleared in Additional
 *
 * An Unsubscribed entry left with no tick is removed. An Additional entry is KEPT even
 * with every tick false (Delta 5b): the user stays listed until a Tower Co user removes
 * the entry. Returns NEW lists `{ additional, unsubscribed }`; `lists` is not changed.
 */
export function applyOverride (lists, contactId, alert, mode) {
  var additional = cloneList(lists && lists.additional)
  var unsubscribed = cloneList(lists && lists.unsubscribed)
  var key = 'c:' + Number(contactId)
  var a = indexOfKey(additional, key)
  var u = indexOfKey(unsubscribed, key)

  if (mode === 'add') {
    if (a === -1) {
      var fresh = additionalEntry(contactId, null, false, false)
      fresh[alert] = true
      additional.push(fresh)
    } else {
      additional[a][alert] = true
    }
    if (u !== -1) {
      unsubscribed[u][alert] = false
      if (!ticked(unsubscribed[u])) {
        unsubscribed.splice(u, 1)
      }
    }
  } else {
    if (u === -1) {
      var gone = unsubscribedEntry(contactId, false, false)
      gone[alert] = true
      unsubscribed.push(gone)
    } else {
      unsubscribed[u][alert] = true
    }
    if (a !== -1) {
      additional[a][alert] = false
    }
  }

  return { additional: additional, unsubscribed: unsubscribed }
}

/**
 * Turn ONE alert of an org user on or off for this request (Delta 5b: the bell and its panel).
 * `user` is a resolved user (resolveParty): `user.alerts[alert]` carries `profile` (the User
 * Profile alert), `added` (their Additional entry has the tick) and `state`. Returns NEW lists
 * `{ additional, unsubscribed }`.
 *
 *   off  added          untick it in Additional and KEEP the entry (even all-false)
 *        profile on     also unsubscribe it, or the profile would keep sending
 *        (profile on, not added: just unsubscribe)
 *   on   unsubscribed   remove the unsubscribe
 *        still wouldn't get it (profile off, not added): tick it in Additional,
 *        creating the entry when there is none ("allow adding")
 */
export function setUserAlert (lists, user, alert, on) {
  var key = 'c:' + Number(user.contact_id)
  var cell = user.alerts[alert]
  var next = {
    additional: cloneList(lists && lists.additional),
    unsubscribed: cloneList(lists && lists.unsubscribed)
  }

  if (on) {
    if (!cell.profile && !cell.added) {
      // Also clears the unsubscribe of this alert, when there is one.
      return applyOverride(next, user.contact_id, alert, 'add')
    }
    if (cell.state === 'unsubscribed') {
      next.unsubscribed = setTick(next.unsubscribed, key, alert, false)
    }
    return next
  }

  if (cell.added) {
    next.additional = setTick(next.additional, key, alert, false, true)
  }
  if (cell.profile) {
    next = applyOverride(next, user.contact_id, alert, 'unsubscribe')
  }
  return next
}

/* ------------------------------------------------------------------------
   Ops (re-applied onto the server's list after a 409, plan R2.9)
------------------------------------------------------------------------ */

function sameTicks (a, b) {
  return !!a.upload === !!b.upload && !!a.approval === !!b.approval
}

/**
 * The ops that turn `before` into `after`, by key: `add` {key, item}, `set` {key,
 * item} (an entry whose ticks changed) and `remove` {key}.
 */
export function diffOps (before, after) {
  var ops = []
  var b = before || []
  var a = after || []
  var i

  for (i = 0; i < b.length; i++) {
    if (indexOfKey(a, recipientKey(b[i])) === -1) {
      ops.push({ type: 'remove', key: recipientKey(b[i]) })
    }
  }

  for (i = 0; i < a.length; i++) {
    var key = recipientKey(a[i])
    var was = indexOfKey(b, key)
    if (was === -1) {
      ops.push({ type: 'add', key: key, item: cloneList([a[i]])[0] })
    } else if (!sameTicks(b[was], a[i])) {
      ops.push({ type: 'set', key: key, item: cloneList([a[i]])[0] })
    }
  }

  return ops
}

/**
 * Re-apply recorded ops onto `list` (the server's current list) and return a NEW
 * list. `add` puts the entry in unless its key is there; `set` replaces by key or
 * adds when missing; `remove` takes it out. Keys are matched with recipientKey, so
 * an org user and a typed address never collide.
 */
export function applyOps (list, ops) {
  var out = cloneList(list)

  for (var o = 0; o < (ops || []).length; o++) {
    var op = ops[o]
    var at = indexOfKey(out, op.key)

    if (op.type === 'remove') {
      if (at !== -1) {
        out.splice(at, 1)
      }
    } else if (op.type === 'set') {
      var item = cloneList([op.item])[0]
      if (at === -1) {
        out.push(item)
      } else {
        out[at] = item
      }
    } else if (op.type === 'add' && at === -1) {
      out.push(cloneList([op.item])[0])
    }
  }

  return out
}

/* ------------------------------------------------------------------------
   The column: who receives what (mirrors RequestMailRecipients, plan §3.3)
------------------------------------------------------------------------ */

function lc (s) {
  return isBlank(s) ? '' : String(s).trim().toLowerCase()
}

/** `{reason}` for an address with no e-mail / a bad format, else null. */
function localInvalid (email) {
  if (isBlank(email)) {
    return { reason: 'no_email' }
  }
  return isValidEmail(email) ? null : { reason: 'bad_format' }
}

// An address that cannot be mailed at all: left out of the "will get" counts.
function unusable (invalid) {
  return !!invalid && (invalid.reason === 'no_email' || invalid.reason === 'bad_format')
}

function byName (a, b) {
  var x = String(a.name || a.email || '').toLowerCase()
  var y = String(b.name || b.email || '').toLowerCase()
  return x < y ? -1 : (x > y ? 1 : 0)
}

function contactState (contact, fallback) {
  if (!contact) {
    return fallback || 'missing'
  }
  return contact.enabled === false ? 'disabled' : 'ok'
}

/**
 * Resolve one party column.
 *
 * @param {Object} model  the read model's party (`GET /actions/{id}/recipients`
 *                        `parties[]`): { party, alerts, users[], additional[],
 *                        unsubscribed[] }. Supplies only LIVE user data; null before
 *                        it has loaded.
 * @param {Object} lists  { additional, unsubscribed }: the request's own lists
 *                        (`actionData.emails.<party>`). They win over the model's
 *                        copy, so a refetch never undoes a pending edit.
 * @return {Object} {
 *   alerts,
 *   users:        [{ contact_id, name, email, is_self, invalid, receivesAny, hasUnsubscribed, hasEntry,
 *                    canTurnOn, group,
 *                    alerts: { upload: {receives, state, profile, added}, approval: {...} } }],
 *                 EVERY user, in the order receiving, noAddress, off, hidden (each A-Z);
 *                 `state` is one of 'on' | 'added' | 'unsubscribed' | 'off'; `profile` / `added`
 *                 say why (the User Profile alert; their Additional entry's tick).
 *                 `hasEntry`: they have an Additional entry, even with every tick false.
 *                 `canTurnOn`: (hasEntry or hasUnsubscribed) and some column alert is Off: the
 *                 bell is a plain bell (Delta 5b).
 *                 Each carries `group`: 'receiving' | 'noAddress' | 'off' | 'hidden'
 *                 (Delta 1, items 5b + 6; the Users list shows only the first two):
 *                   receiving  would receive here and have a usable address (a bad
 *                              format or a failed last send is a real problem: listed), OR
 *                              has an address and is unsubscribed from an alert here, or has an
 *                              Additional entry here, even with every tick false (Delta 5 / 5b:
 *                              they stay in place until the entry is removed; `hasUnsubscribed`,
 *                              `hasEntry`)
 *                   noAddress  would receive here (alert on, or added) but have no address
 *                   off        would receive nothing here, and have an address (even a
 *                              bad one: that is no problem of this request). Not listed,
 *                              but offered by Additional's "Add a user" select.
 *                   hidden     would receive nothing and have no address: never listed
 *   groups:       { receiving, noAddress }  the listed users, by `group`
 *   hiddenCount:  how many users are `hidden`
 *   additional:   [{ key, entry, typed, name, email, state, invalid, contact_id }]
 *   unsubscribed: [{ key, entry, name, email, state, invalid, contact_id, listed }]
 *                 `listed`: the user is a row of groups.receiving (Delta 5: shown there, with a bell)
 *   counts:       { upload, approval }  distinct addresses that will receive and are
 *                 not bad_format / no_email
 *   problems:     distinct recipients that would receive and carry an address problem
 *                 (bad_format / last_failed). A missing address is NOT counted: the
 *                 noAddress group reports those users.
 *   testCount:    distinct addresses a Send Test goes to (any column alert; an
 *                 invalid-format address is still attempted, an empty one is not)
 * }
 */
export function resolveParty (model, lists) {
  var m = model || {}
  var alerts = (m.alerts && m.alerts.length) ? m.alerts.slice() : alertsFor(m.party)
  var additional = (lists && lists.additional) || []
  var unsubscribed = (lists && lists.unsubscribed) || []
  var modelUsers = m.users || []
  var usersById = {}
  var seen = {}
  var counts = { upload: 0, approval: 0 }
  var testSet = {}
  var problemSet = {}
  var i

  var modelEntryInvalid = {}
  var modelEntryContact = {}
  ;(m.additional || []).concat(m.unsubscribed || []).forEach(function (e) {
    var k = recipientKey(e)
    if (k) {
      if (e.invalid) { modelEntryInvalid[k] = e.invalid }
      if (e.contact !== undefined) { modelEntryContact[k] = { contact: e.contact, state: e.state } }
    }
  })

  for (i = 0; i < modelUsers.length; i++) {
    usersById[Number(modelUsers[i].contact_id)] = modelUsers[i]
  }

  // Count one resolved recipient once per address and alert.
  function tally (identity, email, invalid, receives) {
    var any = false
    for (var k = 0; k < alerts.length; k++) {
      var al = alerts[k]
      if (receives[al]) {
        any = true
        if (!unusable(invalid)) {
          var tk = al + '|' + identity
          if (!seen[tk]) {
            seen[tk] = true
            counts[al] += 1
          }
        }
      }
    }
    if (any) {
      if (!isBlank(email)) {
        testSet[lc(email)] = true
      }
      if (invalid && invalid.reason !== 'no_email') {
        problemSet[identity] = true
      }
    }
  }

  // ---- Users (live) ----
  var users = modelUsers.map(function (u) {
    var key = 'c:' + Number(u.contact_id)
    var addEntry = findEntry(additional, key)
    var unsubEntry = findEntry(unsubscribed, key)
    var profile = u.profile || {}
    var out = {
      contact_id: u.contact_id,
      name: u.name,
      email: u.email,
      is_self: !!u.is_self,
      invalid: u.invalid || localInvalid(u.email),
      receivesAny: false,
      hasUnsubscribed: false,
      hasEntry: !!addEntry,
      canTurnOn: false,
      alerts: {}
    }
    var receives = {}

    for (var k = 0; k < alerts.length; k++) {
      var al = alerts[k]
      var fromProfile = !!profile[al]
      var added = !!addEntry && addEntry[al] === true
      var off = !!unsubEntry && unsubEntry[al] === true
      var gets = !off && (fromProfile || added)
      var state = off ? 'unsubscribed' : (gets ? (fromProfile ? 'on' : 'added') : 'off')

      out.alerts[al] = { receives: gets, state: state, profile: fromProfile, added: added }
      receives[al] = gets
      if (gets) {
        out.receivesAny = true
      }
      if (off) {
        out.hasUnsubscribed = true
      }
    }

    out.canTurnOn = (out.hasEntry || out.hasUnsubscribed) && alerts.some(function (a) { return !out.alerts[a].receives })

    tally(lc(u.email) || key, u.email, out.invalid, receives)
    return out
  })

  // ---- Additional entries: a typed address is its own recipient ----
  var additionalRows = additional.map(function (e) {
    var key = recipientKey(e)
    var typed = !(e.contact_id !== undefined && e.contact_id !== null)
    var row = { key: key, entry: e, typed: typed, contact_id: typed ? null : e.contact_id, name: null, email: null, state: 'ok', invalid: null }

    if (typed) {
      row.email = e.email
      row.invalid = modelEntryInvalid[key] || localInvalid(e.email)
      var receives = {}
      for (var k = 0; k < alerts.length; k++) {
        receives[alerts[k]] = e[alerts[k]] === true
      }
      // The same address as a user's is ONE recipient: tally() counts per address.
      tally(lc(e.email), e.email, row.invalid, receives)
    } else {
      var u = usersById[Number(e.contact_id)]
      var held = modelEntryContact[key]
      var contact = u ? { name: u.name, email: u.email, enabled: true } : (held ? held.contact : null)
      row.name = contact ? contact.name : null
      row.email = contact ? contact.email : null
      row.state = u ? 'ok' : contactState(contact, held ? held.state : 'missing')
      row.invalid = u ? (u.invalid || localInvalid(u.email)) : (modelEntryInvalid[key] || null)
    }
    return row
  })

  // ---- Unsubscribed entries (always an org user) ----
  var unsubscribedRows = unsubscribed.map(function (e) {
    var key = recipientKey(e)
    var u = usersById[Number(e.contact_id)]
    var held = modelEntryContact[key]
    var contact = u ? { name: u.name, email: u.email, enabled: true } : (held ? held.contact : null)
    return {
      key: key,
      entry: e,
      typed: false,
      contact_id: e.contact_id,
      name: contact ? contact.name : null,
      email: contact ? contact.email : null,
      state: u ? 'ok' : contactState(contact, held ? held.state : 'missing'),
      invalid: null
    }
  })

  // The groups of the Users list (Delta 1, items 5b + 6).
  var groups = { receiving: [], noAddress: [] }
  var off = []
  var hidden = []

  users.forEach(function (u) {
    var noAddress = !!u.invalid && u.invalid.reason === 'no_email'

    // Delta 5 / 5b: a user unsubscribed from an alert, or holding an Additional entry (even with
    // every tick false), stays in place (the bell turns alerts on or off), so an address-holder
    // with either is listed with the receivers even when they now receive nothing. With no address there is nothing to turn back on: that user is not
    // listed (hidden, or in the no-address group when another alert still reaches them) and the
    // entry is left to the compact Unsubscribed list (`listed: false`).
    if (u.receivesAny) {
      u.group = noAddress ? 'noAddress' : 'receiving'
    } else if (noAddress) {
      u.group = 'hidden'
    } else {
      u.group = (u.hasUnsubscribed || u.hasEntry) ? 'receiving' : 'off'
    }

    ;(u.group === 'hidden' ? hidden : (u.group === 'off' ? off : groups[u.group])).push(u)
  })

  groups.receiving.sort(byName)
  groups.noAddress.sort(byName)

  // An Unsubscribed entry is `listed` when its user is a row of the receiving group (the row's
  // bell turns it back on). The rest (a deleted or disabled contact, a user with no address) are
  // the only ones the Unsubscribed list still shows.
  var listedIds = {}
  groups.receiving.forEach(function (u) { listedIds[Number(u.contact_id)] = true })
  unsubscribedRows.forEach(function (r) { r.listed = listedIds[Number(r.contact_id)] === true })
  off.sort(byName)
  hidden.sort(byName)

  return {
    alerts: alerts,
    users: groups.receiving.concat(groups.noAddress, off, hidden),
    groups: groups,
    hiddenCount: hidden.length,
    additional: additionalRows,
    unsubscribed: unsubscribedRows,
    counts: counts,
    problems: Object.keys(problemSet).length,
    testCount: Object.keys(testSet).length
  }
}
